import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Button, Form, Spinner } from "react-bootstrap";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";   // <-- added

const MockTestModal = ({ show, onClose }) => {
  const [step, setStep] = useState("register"); // "register" | "otp"
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    agree: false,
  });
  const [registeredData, setRegisteredData] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { publicAuth } = useApi();
  const { user } = useAuth();   // <-- logged in user
  const testId = 29;

  // ⭐⭐⭐ DIRECT REDIRECT IF USER LOGGED IN ⭐⭐⭐
  useEffect(() => {
    if (show && user) {
      // Already logged in → direct mock test page
      navigate("/mock-test-page", {
        state: {
          registrationId: user?.id || "",
          testId,
          userDetails: {
            name: user?.name,
            email: user?.email,
            mobile: user?.phone,
          },
        },
      });

      // close modal forcibly
      onClose();
    }
  }, [show, user, navigate]);

  // Timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Body scroll lock
  useEffect(() => {
    if (show) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";

    return () => (document.body.style.overflow = "unset");
  }, [show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const phoneRegex = /^[6-9]\d{9}$/;
    const emailRegex = /^\S+@\S+\.\S+$/;

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!phoneRegex.test(formData.mobile))
      newErrors.mobile = "Enter valid 10-digit mobile";
    if (formData.email && !emailRegex.test(formData.email))
      newErrors.email = "Enter valid email";
    if (!formData.agree) newErrors.agree = "Accept terms";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.mobile,
        email: formData.email,
        test_id: testId,
      };

      const response = await publicAuth("mock-free-register", "POST", payload);

      if (response.data?.status) {
        const registrationId = response.data.data?.id;

        setRegisteredData({ ...formData, registrationId });

        setResendTimer(30);
        setCanResend(false);
        setStep("otp");

        Swal.fire({
          icon: "success",
          title: "OTP Sent!",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire("Error", "Registration failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Enter valid OTP");
      return;
    }

    setLoading(true);
    try {
      const res = await publicAuth("mock/verify-otp", "POST", {
        email: registeredData.email,
        otp: otp,
        id: registeredData.registrationId,
      });

      if (res.data?.status) {
        const { registrationId } = registeredData;

        localStorage.setItem("mockRegistrationId", registrationId);

        Swal.fire({
          icon: "success",
          title: "Verified!",
          timer: 1200,
          showConfirmButton: false,
        });

        onClose();

        setTimeout(() => {
          navigate("/mock-test-page", {
            state: {
              registrationId,
              testId,
              userDetails: {
                name: registeredData.name,
                email: registeredData.email,
                mobile: registeredData.mobile,
              },
            },
            replace: true,
          });
        }, 400);
      } else {
        setOtpError("Invalid OTP");
      }
    } catch (err) {
      setOtpError("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setLoading(true);
    try {
      await publicAuth("mock/resend-otp", "POST", {
        email: registeredData.email,
      });

      setResendTimer(30);
      setCanResend(false);
      setOtp("");
      setOtpError("");

      Swal.fire({
        icon: "success",
        title: "OTP Resent!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Error", "Failed to resend OTP", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToRegister = () => {
    setOtp("");
    setOtpError("");
    setStep("register");
  };

  const resetAndClose = () => {
    setStep("register");
    setFormData({ name: "", mobile: "", email: "", agree: false });
    setOtp("");
    setOtpError("");
    setErrors({});
    setRegisteredData(null);
    setResendTimer(0);
    setCanResend(false);
    onClose();
  };

  if (!show || user) return null; 

  return (
    <>
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
          padding: 20px;
        }
        .modal-box {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          position: relative;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
        .close-btn {
          position: absolute;
          top: 12px;
          right: 16px;
          background: none;
          border: none;
          font-size: 28px;
          color: #666;
          cursor: pointer;
          z-index: 10;
        }
        .close-btn:hover { color: #000; }
        .otp-input {
          letter-spacing: 12px;
          font-size: 1.6rem;
          text-align: center;
          font-weight: bold;
          padding: 12px;
        }
        .resend-link {
          color: #0d6efd;
          text-decoration: underline;
          cursor: pointer;
        }
        .resend-link.disabled {
          color: #6c757d;
          cursor: not-allowed;
          text-decoration: none;
        }
        @media (max-width: 576px) {
          .modal-box { padding: 20px; }
          .otp-input { font-size: 1.4rem; letter-spacing: 8px; }
        }
      `}</style>

      <div className="modal-overlay" onClick={resetAndClose}>
        <div className="modal-box p-4" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn" onClick={resetAndClose}>
            ×
          </button>

          <div className="text-center mb-4">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/mocktest.png`}
              alt="Mock Test"
              style={{ maxHeight: "80px", maxWidth: "100%" }}
              className="mb-3"
            />
            <h5 style={{ color: "#001F8D" }}>
              {step === "otp" ? "Verify OTP" : "Start Mock Test"}
            </h5>
            <p className="text-muted small">
              {step === "otp"
                ? `Enter 6-digit OTP sent to ${registeredData?.email}`
                : "Share details to begin"}
            </p>
          </div>

          {/* === STEP: REGISTER === */}
          {step === "register" && (
            <Form onSubmit={handleRegister}>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Name*"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  isInvalid={!!errors.name}
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Control
                  type="tel"
                  placeholder="Mobile Number*"
                  name="mobile"
                  maxLength={10}
                  value={formData.mobile}
                  onChange={handleChange}
                  isInvalid={!!errors.mobile}
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.mobile}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Control
                  type="email"
                  placeholder="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <p className="text-muted small mt-2">
                Disclaimer: This Mock LLR test is for educational use only.
              </p>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="I agree to the terms and conditions"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleChange}
                  isInvalid={!!errors.agree}
                  disabled={loading}
                />
                {errors.agree && (
                  <div className="text-danger small">{errors.agree}</div>
                )}
              </Form.Group>

              <Button
                type="submit"
                className="w-100"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Registering...
                  </>
                ) : (
                  "START TEST"
                )}
              </Button>
            </Form>
          )}

          {/* === STEP: OTP === */}
          {step === "otp" && (
            <div>
              <div className="mb-3">
                <Form.Control
                  type="text"
                  className={`text-center otp-input ${otpError ? "is-invalid" : ""}`}
                  value={otp}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setOtp(val);
                    setOtpError("");
                  }}
                  placeholder="------"
                  maxLength={6}
                  disabled={loading}
                />
                {otpError && (
                  <div className="text-danger text-center small mt-2">
                    {otpError}
                  </div>
                )}
              </div>

              <div className="text-center mb-3">
                <small className="text-muted">
                  Didn't receive?{" "}
                  <span
                    className={`resend-link ${!canResend || loading ? "disabled" : ""}`}
                    onClick={canResend && !loading ? resendOtp : undefined}
                  >
                    Resend OTP {resendTimer > 0 ? `in ${resendTimer}s` : ""}
                  </span>
                </small>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  className="flex-grow-1"
                  onClick={handleBackToRegister}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  className="flex-grow-1"
                  onClick={verifyOtp}
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Verifying...
                    </>
                  ) : (
                    "VERIFY OTP"
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MockTestModal;